import { useEffect } from 'react'
import useSWR from 'swr'
import { audienceImportSessionsApi } from '@/lib/api'
import type { AudienceCreationFlowSnapshot } from '@/types/components/audiences/import-session'
import { connectWebSocket, onUserImportSessionLatestChanged } from '@/lib/services/websocket'
import { legacyRefreshInterval } from '@/lib/realtime/realtimeKillSwitch'
import { AUDIENCE_IMPORT_LATEST_KEY } from '../lib/cache-keys'

/**
 * SWR-backed hook for the latest resumable import session.
 * Used by the new-audience page for deduplication and the resume card.
 *
 * No interval polling: refetch is driven by focus, visibility, and the
 * user-room `user:import-session-latest-changed` ping when another tab or
 * workflow changes which session is resumable.
 */
export function useLatestSession(options?: {
  /** Revalidate when window regains focus. Default true. */
  revalidateOnFocus?: boolean
}) {
  const revalidateOnFocus = options?.revalidateOnFocus ?? true

  const query = useSWR<AudienceCreationFlowSnapshot | null>(
    AUDIENCE_IMPORT_LATEST_KEY,
    async (_key, options?: { signal?: AbortSignal }) =>
      audienceImportSessionsApi.getLatest(options?.signal),
    {
      refreshInterval: legacyRefreshInterval('IMPORT', 30_000),
      revalidateOnFocus,
      dedupingInterval: 5000,
    },
  )
  const mutateLatestSession = query.mutate

  useEffect(() => {
    connectWebSocket()
    const offLatestChanged = onUserImportSessionLatestChanged(() => {
      void mutateLatestSession()
    })
    return offLatestChanged
  }, [mutateLatestSession])

  useEffect(() => {
    const handler = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        void mutateLatestSession()
      }
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [mutateLatestSession])

  return {
    latestSession: query.data ?? null,
    error: query.error as Error | undefined,
    isLoading: query.isLoading,
    isValidating: query.isValidating,
    mutate: mutateLatestSession,
  }
}
